import {
  ColumnApiModule,
  CsvExportModule,
  ModuleRegistry,
  RenderApiModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import {
  ColumnsToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  LicenseManager,
  ServerSideRowModelModule,
  SetFilterModule,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { getCustomActionExecutions } from 'Apis/library';
import { Typography } from 'Components/atoms';
import { AgGridTableHeader } from 'Components/molecules';
import { ViewQueryModal } from 'Components/organisms/ViewQueryModal';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { EmptyState } from 'Components/molecules/EmptyState';
import EngageIcon from 'Assets/icons/engagement.svg';
import {
  AgGridCustomActionExecutionsProps,
  COLUMN_STATE_STORAGE_KEYS,
  DEFAULT_COL_DEF,
  DEFAULT_COLUMN_STATE,
  GRID_CONFIG,
  findingsTheme,
} from './AgGridCustomActionExecutions.config';
import {
  ActionExecutionAuditRow,
  getCustomActionExecutionsColumns,
} from './getCustomActionExecutionsColumns';
import './styles.scss';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  TextFilterModule,
  TooltipModule,
  SetFilterModule,
  ColumnApiModule,
  RenderApiModule,
  ValidationModule,
  CsvExportModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
]);

const agGridLicenseKey = process.env.REACT_APP_AG_GRID_LICENSE_KEY;
if (agGridLicenseKey) {
  LicenseManager.setLicenseKey(agGridLicenseKey);
}

type PayloadModalState = {
  title: string;
  payload: object;
} | null;

const AgGridCustomActionExecutions: React.FC<AgGridCustomActionExecutionsProps> = ({
  actionSpecId,
  title,
  tableBodyHeight,
  periodDays = 30,
}) => {
  const gridRef = useRef<AgGridReact<ActionExecutionAuditRow>>(null);
  const columnStateLoadedRef = useRef(false);
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [payloadModal, setPayloadModal] = useState<PayloadModalState>(null);

  const openPayloadModal = useCallback((modalTitle: string, payload: unknown) => {
    if (payload === null || payload === undefined) {
      return;
    }
    setPayloadModal({
      title: modalTitle,
      payload: (typeof payload === 'object' ? payload : { value: payload }) as object,
    });
  }, []);

  const columnDefs = useMemo(
    () => getCustomActionExecutionsColumns({ onOpenPayload: openPayloadModal }),
    [openPayloadModal],
  );

  const createServerSideDatasource = useCallback((): IServerSideDatasource => {
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        const { startRow, endRow } = params.request;
        const limit = endRow! - startRow!;
        const offset = startRow!;

        const filter = {
          limit,
          offset,
          periodDays,
        };

        try {
          const response = await getCustomActionExecutions(actionSpecId, filter);

          if (!response) {
            params.fail();
            return;
          }

          const rowData = response.data ?? [];
          const rowCount = response.hints?.count ?? rowData.length;

          setFilteredCount(rowData.length);
          setTotalCount(rowCount);

          params.success({
            rowData,
            rowCount,
          });

          if (rowData.length === 0) {
            gridRef.current?.api?.showNoRowsOverlay();
          } else {
            gridRef.current?.api?.hideOverlay();
          }
        } catch {
          params.fail();
        }
      },
    };
  }, [actionSpecId, periodDays]);

  const datasource = useMemo(() => createServerSideDatasource(), [createServerSideDatasource]);

  const onGridReady = useCallback((params: any) => {
    const savedState = localStorage.getItem(COLUMN_STATE_STORAGE_KEYS.server);
    if (savedState) {
      try {
        params.api.applyColumnState({ state: JSON.parse(savedState), applyOrder: true });
      } catch {
        params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE as any, applyOrder: true });
      }
    } else {
      params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE as any, applyOrder: true });
    }
    columnStateLoadedRef.current = true;
  }, []);

  const handleColumnEvent = useCallback((params: any) => {
    if (!params?.api || !columnStateLoadedRef.current) {
      return;
    }
    if (params.type === 'columnMoved' || params.type === 'columnVisible' || params.finished) {
      const state = params.api.getColumnState();
      localStorage.setItem(COLUMN_STATE_STORAGE_KEYS.server, JSON.stringify(state));
    }
  }, []);

  const handleResetColumns = useCallback(() => {
    const gridApi = gridRef.current?.api;
    if (gridApi) {
      gridApi.applyColumnState({ state: DEFAULT_COLUMN_STATE as any, applyOrder: true });
      const resetState = gridApi.getColumnState();
      localStorage.setItem(COLUMN_STATE_STORAGE_KEYS.server, JSON.stringify(resetState));
    }
  }, []);

  const handleExportCsv = useCallback(() => {
    gridRef.current?.api?.exportDataAsCsv();
  }, []);

  return (
    <div className="h-100 d-flex flex-column custom-action-executions__container">
      <AgGridTableHeader
        title={title}
        rightExtras={
          typeof filteredCount === 'number' && typeof totalCount === 'number' ? (
            <Typography variant="body10" color="text-low-emphasis" className="ag-grid-table-header__results-count">
              <span className="ag-grid-table-header__results-count-number">{filteredCount}</span> results of{' '}
              <span className="ag-grid-table-header__results-count-number">{totalCount}</span> total
            </Typography>
          ) : null
        }
        actionItems={[
          { label: 'Export CSV', onClick: handleExportCsv },
          { label: 'Reset Columns', onClick: handleResetColumns },
        ]}
      />
      <div
        className="ag-theme-alpine flex-grow-1 custom-action-executions__grid-wrapper custom-action-executions__grid-wrapper--theme-findings ag-grid-theme--amp-dark"
        style={{
          height: tableBodyHeight || GRID_CONFIG.defaultHeight,
          minHeight: GRID_CONFIG.minHeight,
        }}
      >
        <AgGridReact<ActionExecutionAuditRow>
          ref={gridRef}
          theme={findingsTheme}
          columnDefs={columnDefs}
          rowModelType="serverSide"
          serverSideDatasource={datasource}
          domLayout="normal"
          suppressAggFuncInHeader={true}
          suppressColumnVirtualisation={false}
          suppressHorizontalScroll={false}
          suppressContextMenu={true}
          animateRows={true}
          defaultColDef={DEFAULT_COL_DEF}
          rowHeight={GRID_CONFIG.rowHeight}
          headerHeight={GRID_CONFIG.headerHeight}
          cacheBlockSize={GRID_CONFIG.cacheBlockSize}
          getRowId={(params) => params.data?.id ?? ''}
          onGridReady={onGridReady}
          onColumnResized={handleColumnEvent}
          onColumnMoved={handleColumnEvent}
          onColumnVisible={handleColumnEvent}
          onColumnPinned={handleColumnEvent}
          sideBar={GRID_CONFIG.sideBar}
          noRowsOverlayComponent={EmptyState}
          noRowsOverlayComponentParams={{
            ImageSrc: EngageIcon,
            noDescription: false,
            description: 'No action executions in the selected period.',
          }}
        />
      </div>
      {payloadModal ? (
        <ViewQueryModal
          show={Boolean(payloadModal)}
          onClose={() => setPayloadModal(null)}
          json={payloadModal.payload}
          isFooter={true}
          title={payloadModal.title}
        />
      ) : null}
    </div>
  );
};

export default AgGridCustomActionExecutions;
